import { useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePrimaryColor,
  usePropertyAddressUrl,
  usePropertyField,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { CTA_OUTLINE, CTA_SOLID, CtaStyles, ctaOverPrimary } from './_cta';
import {
  repliWhiteLogoUrl,
  multihubLogoMonoWhiteUrl,
  multihubLogoMonoBlackUrl,
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
} from '@vibesites/assets';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { stripTrailingCountry } from './PropertyInfoPanel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram } from '@fortawesome/free-brands-svg-icons';
// Local black variant of the Repli wordmark — `@vibesites/assets` only ships
// `repliWhiteLogoUrl`, so we keep a dark-fill copy alongside it for use on
// light primary backgrounds. Source SVG mirrors `repli_white.svg` with the
// wordmark's `fill:#fff` swapped to `#1a1a1a` (matches the dark color used
// by the tokens generator's `getContrastTextColor`).
import repliBlackLogoUrl from '../assets/repli_black.svg';

// ============================================================================
// Contrast text color
// ----------------------------------------------------------------------------
// Mirrors `getContrastTextColor` from `@vibesites/tokens` (defined in
// `vendor/vibesites/tokens/generator.ts`, currently module-private and not
// re-exported, so we replicate the exact logic here verbatim — including the
// `0.55` luminance threshold and the `'#ffffff'` / `'#1a1a1a'` return values).
// Using the same function keeps logo color choice (white vs black) in lockstep
// with the auto-computed `--vibe-color-text-on-primary` CSS variable, so the
// footer's text and logos always pick the same contrast side.
// ============================================================================

const TEXT_ON_PRIMARY_LIGHT = '#ffffff';
const TEXT_ON_PRIMARY_DARK = '#1a1a1a';

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const cleaned = hex.replace('#', '').trim();
  if (cleaned.length === 3) {
    return {
      r: parseInt(cleaned[0] + cleaned[0], 16),
      g: parseInt(cleaned[1] + cleaned[1], 16),
      b: parseInt(cleaned[2] + cleaned[2], 16),
    };
  }
  if (cleaned.length === 6) {
    return {
      r: parseInt(cleaned.slice(0, 2), 16),
      g: parseInt(cleaned.slice(2, 4), 16),
      b: parseInt(cleaned.slice(4, 6), 16),
    };
  }
  return null;
}

function getRelativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const [rs, gs, bs] = [rgb.r / 255, rgb.g / 255, rgb.b / 255].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Returns the contrasting text color (`'#ffffff'` or `'#1a1a1a'`) for a given
 * background hex. Identical to `getContrastTextColor` in `@vibesites/tokens`.
 * Falls back to the white-on-dark side when the input can't be parsed, which
 * matches how the tokens generator treats unknown / missing primary colors.
 */
function getContrastTextColor(bgHex: string | null | undefined): string {
  if (!bgHex) return TEXT_ON_PRIMARY_LIGHT;
  const luminance = getRelativeLuminance(bgHex);
  // Favor white text on medium tones (gold, tan, dusty blue) where dark text is
  // technically higher-contrast but reads as heavy/muddy. Flip to dark only on
  // genuinely light backgrounds (pastels, cream, near-white).
  return luminance > 0.55 ? TEXT_ON_PRIMARY_DARK : TEXT_ON_PRIMARY_LIGHT;
}

// ============================================================================
// Types
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

/** Formats a phone string into (XXX) XXX-XXXX. Passes through if already formatted or unrecognizable. */
function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  // Strip leading country code "1" if 11 digits
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  // Already has am/pm — just normalize casing
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  // Parse 24h "HH:MM" or "H:MM"
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};
const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

interface GroupedHours {
  label: string;
  shortLabel: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  // Index the provided entries by their canonical day so we can fill in the
  // full week below. Standard weekdays not present in the data are treated as
  // "Closed" rather than omitted entirely.
  const byDay = new Map<string, OfficeHour>();
  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (!byDay.has(key)) byDay.set(key, entry);
  }

  // Build the effective list: every canonical day (Mon–Sun) in order, using the
  // supplied entry when present or a synthetic Closed day when missing, followed
  // by any non-standard day entries the property may have defined.
  const canonical: OfficeHour[] = DAY_ORDER.map(
    (day) => byDay.get(day) ?? ({ day } as OfficeHour),
  );
  const extras = raw.filter(
    (entry) => hasValue(entry.day) && !DAY_ORDER.includes(normalizeDay(entry.day)),
  );
  const sorted = [...canonical, ...extras];

  const groups: GroupedHours[] = [];

  for (const entry of sorted) {
    if (!hasValue(entry.day)) continue;

    const key = normalizeDay(entry.day);
    const dayLabel = DAY_LABELS[key] || entry.day;
    const dayShortLabel = DAY_SHORT_LABELS[key] || (entry.day || '').slice(0, 3);
    const isClosed = !hasValue(entry.open_time) && !hasValue(entry.close_time);
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      // Extend the previous group's range — keep both the full and short label
      // ranges in sync so the responsive renderer can swap them at the tablet
      // breakpoint without re-deriving the grouping.
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} - ${dayLabel}`;
      const shortDash = prev.shortLabel.indexOf(' - ');
      const startShort = shortDash !== -1 ? prev.shortLabel.slice(0, shortDash) : prev.shortLabel;
      prev.shortLabel = `${startShort} - ${dayShortLabel}`;
    } else {
      groups.push({ label: dayLabel, shortLabel: dayShortLabel, hours: hoursStr });
    }
  }

  return groups;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_company_mono_white_logo', suggestedFields: ['property_company_mono_white_logo', 'property_company_logo', 'property_company_mono_black_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli Logo' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const poweredByLogoDef: SlotGroupDefinition = {
  label: 'Powered By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multihub Logo' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.multihub.io/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: { label: 'Address Text', type: 'text', defaultField: 'property_full_address', defaultValue: '123 Main Street, City, ST 00000' },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time (mobile-safe Google Maps URL built from place/business/address
    // fields). An explicit override from the editor still takes precedence.
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility Statement' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility-statement' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_facebook_url', suggestedFields: ['property_facebook_url'] },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_instagram_url', suggestedFields: ['property_instagram_url'] },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const residentLoginLinkDef: SlotGroupDefinition = {
  label: 'Resident Login Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_resident_portal_url',
      suggestedFields: ['property_resident_portal_url'],
      defaultValue: '/resident-login',
    },
    text: { label: 'Link Text', type: 'text', defaultValue: 'Resident Login' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
      defaultValue: '/apply-now',
    },
    text: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};


// ============================================================================
// Component
// ============================================================================

export function Footer() {
  const phone = useContentToken('footer.phone');

  // ---- Contrast-driven logo variant selection ----
  // Decide whether text-on-primary should be white or dark using the same
  // `getContrastTextColor` logic the tokens generator uses to compute
  // `--vibe-color-text-on-primary`. We then pick mono_white / mono_black logo
  // defaults (and the Multihub + Repli marks) to match that decision, so the
  // footer logos always sit on the correct contrast side regardless of how
  // dark/light the property's primary brand color is. The runtime-adjusted
  // slot definitions are passed to both `useSlotGroup` and `EditableSlotGroup`
  // so the editor surfaces the same default the renderer is using.
  const primaryColor = usePrimaryColor();
  const textOnPrimary = getContrastTextColor(primaryColor);
  const useWhiteOnPrimary = textOnPrimary === TEXT_ON_PRIMARY_LIGHT;

  const runtimeLogoSlotDef: SlotGroupDefinition = useWhiteOnPrimary
    ? logoSlotDef
    : {
        ...logoSlotDef,
        fields: {
          ...logoSlotDef.fields,
          image: {
            ...logoSlotDef.fields.image,
            defaultField: 'property_mono_black_logo',
          },
        },
      };

  const runtimeManagedByLogoDef: SlotGroupDefinition = useWhiteOnPrimary
    ? managedByLogoDef
    : {
        ...managedByLogoDef,
        fields: {
          ...managedByLogoDef.fields,
          image: {
            ...managedByLogoDef.fields.image,
            defaultField: 'property_company_mono_black_logo',
          },
        },
      };

  const logo = useSlotGroup('footer.logo', runtimeLogoSlotDef);
  const managedByLogo = useSlotGroup('footer.managed_by_logo', runtimeManagedByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const poweredByLogo = useSlotGroup('footer.powered_by_logo', poweredByLogoDef);
  const addressLink = useSlotGroup('footer.address', addressLinkDef);
  // Fallback URL when the editor hasn't set a custom address link — the
  // hook builds a mobile-safe Google Maps URL from property place / business /
  // full-address / lat-lon fields, in that order.
  const propertyAddressUrl = usePropertyAddressUrl();
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);
  const facebookLink = useSlotGroup('footer.facebook_link', facebookLinkDef);
  const instagramLink = useSlotGroup('footer.instagram_link', instagramLinkDef);
  const residentLoginLink = useSlotGroup('footer.resident_login_link', residentLoginLinkDef);
  const applyNowLink = useSlotGroup('footer.apply_now_link', applyNowLinkDef);

  // ---- "Managed by" company logo + link fallbacks ----
  // The slot group's `image` resolves the mono-white / mono-black company logo
  // via defaultField, but some properties only populate the full-color
  // `property_company_logo` — fall back to it so the credit still renders. The
  // link falls back to the company website so the mark always points at the
  // management company.
  const companyLogoFull = usePropertyField('property_company_logo');
  const companyUrl = usePropertyField('property_company_url');
  const managedByImage = managedByLogo.image || companyLogoFull || '';
  const managedByHref = managedByLogo.url || companyUrl || '';

  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });

  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  // ─── RentCafe IQ chatbot z-index pin ────────────────────────────────────────
  // The Yardi RentCafe IQ widget is injected at runtime by an external script.
  // It (a) ships under several DOM signatures across versions and (b) sets its
  // own inline `style="z-index: …"` on its outer container after mount, which
  // beats anything a stylesheet declares. The CSS rule further down in the
  // render covers the common selectors as a cheap first pass; this effect is
  // the authoritative fix.
  //
  // Approach: watch document.body for any added/changed node. For each element
  // whose id/class/src/title/name matches a RentCafe-IQ-ish pattern, find the
  // outermost `position: fixed` ancestor (the actual floating container, which
  // is what controls stacking against the rest of the page) and pin its
  // z-index to the max signed 32-bit int with `!important`. `setProperty` with
  // the `important` priority is the only way to override an inline style set
  // by the widget's own JS.
  //
  // Scoped to the Footer because Footer is rendered on every page via
  // DefaultLayout, so we get a single global mount point without having to
  // introduce a new always-on component.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    // Patterns covering the DOM signatures Yardi ships the RentCafe IQ /
    // "RentCafe Connect" webchat widget under. The current production build
    // uses the `rcc-app-*` class prefix and the `conversations-webchat-toggle`
    // / `webchat-toggle-button` launcher; older / alternate builds have
    // surfaced under rentcafe / rciq / CRM IQ / Yardi / Yvenue branding, so
    // we keep those entries too as a defense against future rebrands.
    const PATTERNS = [
      /\brcc-app/i,
      /\bwebchat-toggle/i,
      /conversations-webchat/i,
      /rentcafe/i,
      /rciq/i,
      /\bcrm[-_ ]?iq\b/i,
      /\biq[-_ ]?(chat|bot)/i,
      /yardi/i,
      /yvenue/i,
    ];

    const Z = '2147483647';

    function attrHaystack(el: Element): string {
      const id = el.id || '';
      const cls = typeof el.className === 'string'
        ? el.className
        : ((el.className as unknown as { baseVal?: string })?.baseVal || '');
      const src = el.getAttribute('src') || '';
      const title = el.getAttribute('title') || '';
      const name = el.getAttribute('name') || '';
      // Include data-* attributes — Yardi widgets sometimes only brand via those.
      let data = '';
      for (const a of Array.from(el.attributes)) {
        if (a.name.startsWith('data-')) data += ` ${a.name} ${a.value}`;
      }
      return `${id} ${cls} ${src} ${title} ${name}${data}`;
    }

    function looksLikeRciq(el: Element): boolean {
      const hay = attrHaystack(el);
      return PATTERNS.some(p => p.test(hay));
    }

    function outermostFixedAncestor(el: Element): HTMLElement | null {
      let cur: Element | null = el;
      let last: HTMLElement | null = null;
      while (cur && cur !== document.body && cur instanceof HTMLElement) {
        const pos = getComputedStyle(cur).position;
        if (pos === 'fixed') last = cur;
        cur = cur.parentElement;
      }
      return last;
    }

    function pin(el: HTMLElement) {
      el.style.setProperty('z-index', Z, 'important');
    }

    function processCandidate(el: Element) {
      if (!(el instanceof HTMLElement)) return;
      if (!looksLikeRciq(el)) return;
      // Pin the matched element itself (in case it's already the fixed container)
      // and also the outermost fixed ancestor (covers wrappers / portals).
      pin(el);
      const fixedAncestor = outermostFixedAncestor(el);
      if (fixedAncestor && fixedAncestor !== el) pin(fixedAncestor);
    }

    function scanSubtree(root: ParentNode) {
      // Scope the query so we don't enumerate the whole document on every tick.
      const candidates = root.querySelectorAll('iframe, div, aside, section, button');
      for (const c of Array.from(candidates)) processCandidate(c);
      // The root node itself can also be a match (querySelectorAll skips it).
      if (root instanceof Element) processCandidate(root);
    }

    // Initial pass — catches the widget if it's already mounted by the time
    // the Footer hydrates (e.g. injected from <head> before React boots).
    scanSubtree(document.body);

    // Coalesce bursts of mutations into a single rAF-scheduled scan so we
    // don't thrash on chat widgets that animate (every transform tick fires
    // an attribute mutation).
    let scheduled = false;
    const observer = new MutationObserver((records) => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        for (const r of records) {
          for (const node of Array.from(r.addedNodes)) {
            if (node instanceof Element) scanSubtree(node);
          }
          if (r.type === 'attributes' && r.target instanceof Element) {
            processCandidate(r.target);
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      // Only react to attribute changes that could affect identification or
      // stacking — anything else (e.g. inline transforms during animation) is
      // noise we want to ignore.
      attributeFilter: ['id', 'class', 'src', 'title', 'name', 'style'],
    });

    return () => observer.disconnect();
  }, []);

  return (
    <footer
      className="text-primary-foreground"
      style={{ backgroundColor: 'var(--vibe-color-primary)' }}
    >

      {/* ---- Top Section ----
          Two clusters spread edge-to-edge on desktop (justify-between),
          stacked + centered on mobile:
            Left cluster  : community logo + social badges | Resident Login /
                            Apply Now CTAs
            Right cluster : LOCATION | CONTACT | OFFICE HOURS info columns
          Section headings use the SECONDARY brand color as a gold-style accent
          against the PRIMARY footer background (matches the reference, where
          primary is the green field and secondary is the gold heading tone). */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-10 md:pt-14">
        <div className="flex flex-col items-center gap-12 lg:flex-row lg:items-start lg:justify-between lg:gap-10">

          {/* ---- Left cluster: brand + CTAs ---- */}
          <div className="flex flex-col items-center gap-8 sm:flex-row sm:items-start sm:gap-10">

            {/* Logo + social badges */}
            <div className="flex flex-col items-center gap-5 sm:items-start">
              <EditableSlotGroup groupPath="footer.logo" definition={runtimeLogoSlotDef}>
                <a href="/" className="block">
                  {logo.image ? (
                    <VibesitesImage
                      url={logo.image}
                      alt={logo.alt || 'Community Logo'}
                      className="h-24 w-auto max-w-[240px] object-contain"
                      sizes="240px"
                    />
                  ) : (
                    <div className="font-heading text-3xl text-primary-foreground">Our Community</div>
                  )}
                </a>
              </EditableSlotGroup>

              {/* Social badges — filled light tint of the primary background,
                  icon in the full primary color. Render only when a URL set. */}
              {(facebookLink.url || instagramLink.url) && (
                <div className="flex items-center gap-3">
                  {facebookLink.url && (
                    <EditableSlotGroup groupPath="footer.facebook_link" definition={facebookLinkDef}>
                      <a
                        href={facebookLink.url}
                        target={facebookLink._target || '_blank'}
                        rel="noopener noreferrer"
                        aria-label={facebookLink.label || 'Facebook'}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-full transition-opacity hover:opacity-90"
                        style={{ backgroundColor: 'color-mix(in srgb, var(--vibe-color-primary) 30%, #ffffff)' }}
                      >
                        <FontAwesomeIcon icon={faFacebook} className="w-4 h-4 text-[var(--vibe-color-primary)]" />
                      </a>
                    </EditableSlotGroup>
                  )}
                  {instagramLink.url && (
                    <EditableSlotGroup groupPath="footer.instagram_link" definition={instagramLinkDef}>
                      <a
                        href={instagramLink.url}
                        target={instagramLink._target || '_blank'}
                        rel="noopener noreferrer"
                        aria-label={instagramLink.label || 'Instagram'}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-full transition-opacity hover:opacity-90"
                        style={{ backgroundColor: 'color-mix(in srgb, var(--vibe-color-primary) 30%, #ffffff)' }}
                      >
                        <FontAwesomeIcon icon={faInstagram} className="w-4 h-4 text-[var(--vibe-color-primary)]" />
                      </a>
                    </EditableSlotGroup>
                  )}
                </div>
              )}
            </div>

            {/* CTAs — Resident Login (solid) + Apply Now (outline), both keyed
                to the canonical CTA styles over the primary footer surface. */}
            <div className="flex w-full max-w-[220px] flex-col gap-3 sm:w-[220px]" style={ctaOverPrimary}>
              <CtaStyles />
              {/* On hover, fill background + border with the resolved secondary
                  hover fill (`--cta-hover-fill`, published by <CtaStyles/> and
                  auto-darkened when a white label wouldn't clear WCAG AA on the
                  raw secondary) and keep the label white. Higher specificity
                  than the shared .v-cta--solid:hover, and the ::before darkening
                  overlay is suppressed so the fill shows. */}
              <style>{`
                .resident-login-cta.v-cta--solid:hover {
                  color: #ffffff;
                  background: var(--cta-hover-fill, var(--vibe-color-secondary));
                  border-color: var(--cta-hover-fill, var(--vibe-color-secondary));
                }
                .resident-login-cta.v-cta--solid:hover::before {
                  opacity: 0;
                }
              `}</style>
              <EditableSlotGroup groupPath="footer.resident_login_link" definition={residentLoginLinkDef} className="w-full">
                <a
                  href={residentLoginLink.url || '/resident-login'}
                  target={residentLoginLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className={`w-full resident-login-cta ${CTA_SOLID}`}
                >
                  {residentLoginLink.text || 'Resident Login'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="footer.apply_now_link" definition={applyNowLinkDef} className="w-full">
                <a
                  href={applyNowLink.url || '/apply-now'}
                  target={applyNowLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className={`w-full ${CTA_OUTLINE}`}
                >
                  {applyNowLink.text || 'Apply Now'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* ---- Right cluster: info columns ---- */}
          <div className="flex flex-col items-center gap-10 text-center sm:flex-row sm:items-start sm:text-left sm:gap-10 lg:gap-14">

            {/* LOCATION */}
            <div className="flex flex-col">
              <RichTextContent path="footer.location_label" defaultValue="<p>Location</p>" as="h3" className="font-heading text-xl font-semibold uppercase tracking-wide text-primary-foreground mb-3" />
              <EditableSlotGroup groupPath="footer.address" definition={addressLinkDef}>
                <a
                  href={addressLink.url || propertyAddressUrl || '#'}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="font-body text-[15px] text-primary-foreground hover:opacity-80 transition-opacity block max-w-[240px]"
                >
                  {(() => {
                    const full = stripTrailingCountry(addressLink.text || '123 Main Street, City, ST 00000');
                    // Split the single-line address at the first comma so the
                    // street stays on line 1 and the city/state/zip drop to a
                    // second line. The second span is a block at every breakpoint
                    // AND whitespace-nowrap so the whole "City, ST 00000" stays
                    // together on one line — otherwise the segment wraps
                    // naturally and the zip can fall onto a line by itself on
                    // mobile.
                    const idx = full.indexOf(',');
                    if (idx === -1) return full;
                    const street = full.slice(0, idx);
                    const rest = full.slice(idx + 1).trim();
                    return (
                      <>
                        <span>{street},</span>{' '}
                        <span className="block whitespace-nowrap">{rest}</span>
                      </>
                    );
                  })()}
                </a>
              </EditableSlotGroup>
            </div>

            {/* CONTACT */}
            <div className="flex flex-col">
              <RichTextContent path="footer.contact_label" defaultValue="<p>Contact</p>" as="h3" className="font-heading text-xl font-semibold uppercase tracking-wide text-primary-foreground mb-3" />
              <a
                href={`tel:${(phone || '').replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref font-body text-[15px] tracking-normal sm:tracking-wide whitespace-nowrap text-primary-foreground hover:opacity-80 transition-opacity"
                style={{ fontFamily: 'var(--vibe-font-body)' }}
              >
                <EditableToken
                  path="footer.phone"
                  defaultField="property_call_tracking_phone"
                  defaultValue="(555) 555-5555"
                >
                  {formatPhone(phone)}
                </EditableToken>
              </a>
            </div>

            {/* OFFICE HOURS */}
            <div className="flex flex-col">
              <RichTextContent path="footer.office_hours_label" defaultValue="<p>Office Hours</p>" as="h3" className="font-heading text-xl font-semibold uppercase tracking-wide text-primary-foreground mb-3" />
              {groupedHours.length > 0 ? (
                <dl className="space-y-1.5 w-full min-w-0 sm:min-w-[200px] lg:min-w-[240px] max-w-[300px]">
                  {groupedHours.map((group, index) => (
                    <div key={index} className="flex justify-between gap-4 sm:gap-8 font-body text-[15px] text-primary-foreground">
                      <dt>
                        {/* Short ranges (Mon - Wed) on the narrow tablet columns,
                            full day names once there's room at lg+. */}
                        <span className="lg:hidden">{group.shortLabel}</span>
                        <span className="hidden lg:inline">{group.label}</span>
                      </dt>
                      <dd className="text-right whitespace-nowrap">{group.hours}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                // Office hours are driven entirely by the `office_hours`
                // collection binding above. When the property has no hours,
                // show a simple placeholder rather than a manual override.
                <p className="font-body text-sm text-[var(--vibe-color-text-on-primary)]/60">Office hours will be displayed here.</p>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* ---- Bottom Bar ----
          A single centered column: legal nav + compliance icons on top, then
          the three partner credits (Managed By / Website Design By / Powered
          By) grouped together on ONE row directly beneath them. The credits
          row uses `flex-wrap` + `justify-center` so it stays on one line on
          desktop and wraps gracefully (still centered) on narrow screens. */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        <div className="flex flex-col items-center gap-8">

          {/* Legal nav + compliance icons */}
          <div className="flex flex-col items-center gap-4">
            {/* Keep the three legal links on a single line at every width
                (`flex-nowrap`), tightening type size / tracking / gaps on
                mobile so the row fits without wrapping. Each link is marked
                `whitespace-nowrap` so its label never breaks internally. */}
            <nav className="flex flex-nowrap items-center justify-center gap-x-1.5 sm:gap-x-2 gap-y-1 font-body text-[10px] sm:text-[12px] tracking-normal sm:tracking-wide uppercase text-primary-foreground" aria-label="Legal links">
              <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
                <a href={privacyPolicyLink.url || '/privacy-policy'} target={privacyPolicyLink._target || '_self'} className="whitespace-nowrap hover:opacity-70 transition-opacity">
                  {privacyPolicyLink.text || 'Privacy Policy'}
                </a>
              </EditableSlotGroup>
              <span aria-hidden="true" className="text-[var(--vibe-color-text-on-primary)]/40">|</span>
              <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
                <a href={accessibilityLink.url || '/accessibility-statement'} target={accessibilityLink._target || '_self'} className="whitespace-nowrap hover:opacity-70 transition-opacity">
                  {accessibilityLink.text || 'Accessibility Statement'}
                </a>
              </EditableSlotGroup>
              <span aria-hidden="true" className="text-[var(--vibe-color-text-on-primary)]/40">|</span>
              <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
                <a href={sitemapLink.url || '/sitemap'} target={sitemapLink._target || '_self'} className="whitespace-nowrap hover:opacity-70 transition-opacity">
                  {sitemapLink.text || 'Sitemap'}
                </a>
              </EditableSlotGroup>
            </nav>
            <div className="flex items-center justify-center gap-6 text-primary-foreground">
              <WheelchairIcon size={26} color="currentColor" />
              <FairHousingIcon size={26} color="currentColor" />
              <PetsAllowedIcon size={26} color="currentColor" />
            </div>
          </div>

          {/* Partner credits — Managed By, Website Design By (Repli) and
              Powered By (Multihub) grouped together on one centered row.
              Generous horizontal spacing (scaling up at each breakpoint)
              keeps the airy, spread-out vibe of the original footer. */}
          <div className="flex flex-wrap items-center justify-center gap-x-12 sm:gap-x-20 lg:gap-x-28 gap-y-4">

            {/* Managed By — pulls the management company logo (mono variant via
                the slot, falling back to the full-color company logo) and links
                to the company website. */}
            <EditableSlotGroup
              groupPath="footer.managed_by_logo"
              definition={runtimeManagedByLogoDef}
              className="flex justify-center"
            >
              {managedByImage ? (
                <a
                  href={managedByHref || '#'}
                  target={managedByLogo._target || '_blank'}
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 opacity-90 hover:opacity-100 transition-opacity"
                >
                  <RichTextContent
                    path="footer.managed_by_text"
                    defaultValue="<p>Managed by</p>"
                    as="span"
                    className="font-body text-[11px] uppercase tracking-[0.2em] text-primary-foreground whitespace-nowrap"
                  />
                  <VibesitesImage
                    url={managedByImage}
                    alt={managedByLogo.alt || 'Property Management Company'}
                    className="h-7 w-auto max-w-[140px] object-contain"
                    sizes="140px"
                  />
                </a>
              ) : null}
            </EditableSlotGroup>

            {/* Website Design By (Repli) */}
            <EditableSlotGroup
              groupPath="footer.design_by_logo"
              definition={designByLogoDef}
              className="flex justify-center"
            >
              <a
                href={designByLogo.url || 'https://www.repli360.com/'}
                target={designByLogo._target || '_blank'}
                rel="noopener noreferrer"
                className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
              >
                <RichTextContent
                  path="footer.design_by_text"
                  defaultValue="<p>Website design by</p>"
                  as="span"
                  className="font-body text-[11px] uppercase tracking-[0.2em] text-primary-foreground whitespace-nowrap"
                />
                <VibesitesImage
                  url={useWhiteOnPrimary ? repliWhiteLogoUrl : repliBlackLogoUrl}
                  alt={designByLogo.alt || 'Repli Logo'}
                  height={16}
                  className="h-4 w-auto"
                  sizes="120px"
                  priority
                />
              </a>
            </EditableSlotGroup>

            {/* Powered By (Multihub) — platform credit. Mirrors the Repli mark:
                the mono logo variant follows the same contrast decision as the
                rest of the footer so it sits correctly on light or dark
                primary backgrounds. */}
            <EditableSlotGroup
              groupPath="footer.powered_by_logo"
              definition={poweredByLogoDef}
              className="flex justify-center"
            >
              <a
                href={poweredByLogo.url || 'https://www.multihub.io/'}
                target={poweredByLogo._target || '_blank'}
                rel="noopener noreferrer"
                className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
              >
                <RichTextContent
                  path="footer.powered_by_text"
                  defaultValue="<p>Powered by</p>"
                  as="span"
                  className="font-body text-[11px] uppercase tracking-[0.2em] text-primary-foreground whitespace-nowrap"
                />
                <VibesitesImage
                  url={useWhiteOnPrimary ? multihubLogoMonoWhiteUrl : multihubLogoMonoBlackUrl}
                  alt={poweredByLogo.alt || 'Multihub Logo'}
                  height={16}
                  className="h-4 w-auto"
                  sizes="120px"
                />
              </a>
            </EditableSlotGroup>

          </div>
        </div>
      </div>

      {/* ---- Global z-index fallback for the RentCafe IQ chatbot ----
          This is a cheap first pass to cover the common DOM signatures Yardi
          has shipped (iframe, container div, launcher button). The
          authoritative fix lives in the `useEffect` above this `return` — it
          uses a MutationObserver and inline `!important` to handle widget
          variants whose selectors we don't know and to beat the inline
          z-index the widget sets via its own JS after mount. */}
      <style>{`
        /* Production RentCafe Connect / IQ build (confirmed against live DOM):
           the floating wrap is .rcc-app-cta-wrap, the launcher is
           #conversations-webchat-toggle.webchat-toggle-button. We target the
           wrap (and the inner controls layer) so the entire stacking context
           gets lifted, not just the bubble. */
        .rcc-app-cta-wrap,
        .rcc-app-controls,
        [class*="rcc-app-"],
        [id*="conversations-webchat"],
        [class*="webchat-toggle"],
        /* Legacy / alternate Yardi signatures, kept as a hedge against
           rebrands and per-property build variants. */
        iframe[src*="rentcafeiq"],
        iframe[src*="rentcafe-iq"],
        iframe[src*="rentcafe.com/iq"],
        iframe[src*="yardi"],
        iframe[src*="yvenue"],
        iframe[title*="RentCafe IQ" i],
        iframe[title*="Rent Cafe IQ" i],
        iframe[title*="CRM IQ" i],
        iframe[title*="webchat" i],
        [id^="rciq"],
        [id*="rentcafeiq"],
        [id*="rentcafe-iq"],
        [id*="rentcafe_iq"],
        [id*="iq-chat"],
        [id*="iqchat"],
        [id*="iq-bot"],
        [id*="iqbot"],
        [class^="rciq-"],
        [class*=" rciq-"],
        [class*="rentcafeiq"],
        [class*="rentcafe-iq"],
        [class*="rentcafe_iq"],
        [class*="iq-chat"],
        [class*="iqchat"],
        [class*="yardi-chat"],
        [class*="yvenue"],
        [data-rciq],
        [data-rentcafeiq] {
          z-index: 2147483647 !important;
        }
      `}</style>

    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'footer.location_label': {
      label: 'Location Label',
      type: 'richtext',
      defaultValue: '<p>Location</p>',
    },
    'footer.contact_label': {
      label: 'Contact Label',
      type: 'richtext',
      defaultValue: '<p>Contact</p>',
    },
    'footer.office_hours_label': {
      label: 'Office Hours Label',
      type: 'richtext',
      defaultValue: '<p>Office Hours</p>',
    },
    'footer.managed_by_text': {
      label: 'Managed By Text',
      type: 'richtext',
      defaultValue: '<p>Managed by</p>',
    },
    'footer.design_by_text': {
      label: 'Website Design By Text',
      type: 'richtext',
      defaultValue: '<p>Website design by</p>',
    },
    'footer.powered_by_text': {
      label: 'Powered By Text',
      type: 'richtext',
      defaultValue: '<p>Powered by</p>',
    },
  },
  slotGroups: {
    'footer.logo': logoSlotDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.powered_by_logo': poweredByLogoDef,
    'footer.address': addressLinkDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
    'footer.facebook_link': facebookLinkDef,
    'footer.instagram_link': instagramLinkDef,
    'footer.resident_login_link': residentLoginLinkDef,
    'footer.apply_now_link': applyNowLinkDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as any).contentSchema = contentSchema;

export default Footer;
